import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "loadingIndicator", "markAllButton"]
  static values = { 
    refreshUrl: String,
    autoRefresh: { type: Boolean, default: false },
    refreshInterval: { type: Number, default: 30000 } // 30 seconds
  }

  connect() {
    if (this.autoRefreshValue) {
      this.startAutoRefresh()
    }
  }

  disconnect() {
    this.stopAutoRefresh()
  }

  startAutoRefresh() {
    this.refreshTimer = setInterval(() => {
      this.refreshNotifications()
    }, this.refreshIntervalValue)
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  async refreshNotifications() {
    if (!this.refreshUrlValue) return

    try {
      this.showLoading()
      
      const response = await fetch(this.refreshUrlValue, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        this.listTarget.innerHTML = html
        this.hideLoading()
      }
    } catch (error) {
      console.error("Failed to refresh notifications:", error)
      this.hideLoading()
    }
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove("hidden")
    }
    
    // Add subtle loading state to list
    if (this.hasListTarget) {
      this.listTarget.style.opacity = "0.7"
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden")
    }
    
    // Remove loading state
    if (this.hasListTarget) {
      this.listTarget.style.opacity = "1"
    }
  }

  markAllAsRead(event) {
    // Add optimistic UI update
    const unreadItems = this.listTarget.querySelectorAll('[data-notification-unread="true"]')
    unreadItems.forEach(item => {
      item.dataset.notificationUnread = "false"
      // Remove unread styling
      item.classList.remove('bg-indigo-50/30', 'dark:bg-indigo-900/10')
      // Hide unread indicators
      const indicators = item.querySelectorAll('.bg-indigo-500')
      indicators.forEach(indicator => indicator.style.display = 'none')
    })

    // Update counts immediately for better UX
    const countBadges = document.querySelectorAll('[data-notification-count]')
    countBadges.forEach(badge => {
      badge.textContent = '0'
      badge.style.display = 'none'
    })

    // Hide the mark all button
    if (this.hasMarkAllButtonTarget) {
      this.markAllButtonTarget.style.display = 'none'
    }
  }

  // Handle individual notification interactions
  markNotificationAsRead(event) {
    const notificationElement = event.target.closest('[data-notification-id]')
    if (notificationElement) {
      notificationElement.dataset.notificationUnread = "false"
      notificationElement.classList.remove('bg-indigo-50/30', 'dark:bg-indigo-900/10')
      
      // Hide unread indicator
      const indicator = notificationElement.querySelector('.bg-indigo-500')
      if (indicator) {
        indicator.style.display = 'none'
      }

      // Update count (simple decrement)
      this.decrementNotificationCount()
    }
  }

  decrementNotificationCount() {
    const countBadges = document.querySelectorAll('[data-notification-count]')
    countBadges.forEach(badge => {
      const currentCount = parseInt(badge.textContent) || 0
      const newCount = Math.max(0, currentCount - 1)
      badge.textContent = newCount
      
      if (newCount === 0) {
        badge.style.display = 'none'
      }
    })
  }

  // Handle clicks on notification items
  handleNotificationClick(event) {
    const notificationElement = event.currentTarget
    const actionUrl = notificationElement.dataset.actionUrl
    
    if (actionUrl) {
      // Mark as read optimistically
      this.markNotificationAsRead(event)
      
      // Navigate to the action URL
      window.location.href = actionUrl
    }
  }
}
